import React, { useState } from 'react';
import { LogIn, Mail, Lock, AlertCircle, Users, UserCircle } from 'lucide-react';
import { getApiUrl } from '../utils/auth';

const Login = ({ onLogin }) => {
  const [userType, setUserType] = useState('staff');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${getApiUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          userType,
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user, data.token);
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/images/YDG_LOGO.png"
              alt="YDG Logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text mb-2">
            Commission Management System
          </h1>
          <p className="text-gray-600 dark:text-dark-muted">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="neo-raised p-8 transition-colors duration-200">
          {/* Staff / Investor selector – animated pill */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-3 text-center">
              Sign in as
            </label>
            <div className="relative flex rounded-2xl neo-inset p-1">
              <div
                className="absolute top-1 bottom-1 rounded-lg bg-primary-600 shadow-md transition-all duration-300 ease-out"
                style={{
                  left: userType === 'staff' ? '4px' : '50%',
                  width: 'calc(50% - 4px)',
                }}
              />
              <button
                type="button"
                onClick={() => { setUserType('staff'); setError(''); }}
                className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  userType === 'staff'
                    ? 'text-white'
                    : 'text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text'
                }`}
              >
                <Users className="w-4 h-4" />
                Staff
              </button>
              <button
                type="button"
                onClick={() => { setUserType('investor'); setError(''); }}
                className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  userType === 'investor'
                    ? 'text-white'
                    : 'text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text'
                }`}
              >
                <UserCircle className="w-4 h-4" />
                Investor
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-dark-muted" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 neo-field dark:text-dark-text"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-dark-muted" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 neo-field dark:text-dark-text"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full neo-btn-primary font-semibold py-3 px-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/*           {userType === 'staff' && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
              <p className="text-xs text-gray-500 dark:text-dark-muted text-center">
                Default credentials: superadmin@ydg.com / Admin@123
              </p>
            </div>
          )}
          {userType === 'investor' && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
              <p className="text-xs text-gray-500 dark:text-dark-muted text-center">
                Forgot your password? Contact your referral or account contact to get a new setup link.
              </p>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default Login;
